import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  createModeratedWord,
  deleteModeratedWord,
  editModeratedWord,
  getForbiddenAndModeratedWords,
  createBlockedWord,
  editBlockedWord,
  deleteBlockedWord
} from '~/api/mangeWords';
import { getBuildingNames } from '~/api/building';
import { WordCategory } from '~/containers/ManageWords/components/ManageWordsList';
import CobuConstants from '~/helpers/cobuConstants';
import { ModeratedWord, WordLanguage } from '~/types/manageWords';

export type BuildingOption = {
  id: string;
  name: string;
};

type LoadingConfig = {
  flag: boolean;
  comment?: string;
};

const defaultLoadingConfig: LoadingConfig = {
  flag: false,
  comment: ''
};

const GLOBAL_SCOPE = 'global';

const WORD_CATEGORY_LABELS: Record<
  WordCategory,
  {
    creating: string;
    editing: string;
    deleting: string;
    createSuccess: string;
    editSuccess: string;
    deleteSuccess: string;
  }
> = {
  forbidden: {
    creating: CobuConstants.manageWords.creatingForbiddenWord,
    editing: CobuConstants.manageWords.editingForbiddenWord,
    deleting: CobuConstants.manageWords.deletingForbiddenWord,
    createSuccess: CobuConstants.manageWords.createForbiddenWordSuccess,
    editSuccess: CobuConstants.manageWords.editForbiddenWordSuccess,
    deleteSuccess: CobuConstants.manageWords.deleteForbiddenWordSuccess
  },
  moderated: {
    creating: CobuConstants.manageWords.creatingModeratedWord,
    editing: CobuConstants.manageWords.editingModeratedWord,
    deleting: CobuConstants.manageWords.deletingModeratedWord,
    createSuccess: CobuConstants.manageWords.createModeratedWordSuccess,
    editSuccess: CobuConstants.manageWords.editModeratedWordSuccess,
    deleteSuccess: CobuConstants.manageWords.deleteModeratedWordSuccess
  },
  blocked: {
    creating: CobuConstants.manageWords.creatingBlockedWord,
    editing: CobuConstants.manageWords.editingBlockedWord,
    deleting: CobuConstants.manageWords.deletingBlockedWord,
    createSuccess: CobuConstants.manageWords.createBlockedWordSuccess,
    editSuccess: CobuConstants.manageWords.editBlockedWordSuccess,
    deleteSuccess: CobuConstants.manageWords.deleteBlockedWordSuccess
  },
  alwaysHide: {
    creating: CobuConstants.manageWords.creatingAlwaysHideWord,
    editing: CobuConstants.manageWords.editingAlwaysHideWord,
    deleting: CobuConstants.manageWords.deletingAlwaysHideWord,
    createSuccess: CobuConstants.manageWords.createAlwaysHideWordSuccess,
    editSuccess: CobuConstants.manageWords.editAlwaysHideWordSuccess,
    deleteSuccess: CobuConstants.manageWords.deleteAlwaysHideWordSuccess
  }
};

export default function useManageWords() {
  const [forbiddenWords, setForbiddenWords] = useState<ModeratedWord[]>([]);
  const [moderatedWords, setModeratedWords] = useState<ModeratedWord[]>([]);
  const [blockedWords, setBlockedWords] = useState<ModeratedWord[]>([]);
  const [alwaysHideWords, setAlwaysHideWords] = useState<ModeratedWord[]>([]);
  const [buildingOptions, setBuildingOptions] = useState<BuildingOption[]>([]);
  const [selectedBuildingId, setSelectedBuildingId] =
    useState<string>(GLOBAL_SCOPE);
  const [loadingConfig, setLoadingConfig] =
    useState<LoadingConfig>(defaultLoadingConfig);

  useEffect(() => {
    fetchBuildings();
  }, []);

  useEffect(() => {
    fetchForbiddenAndModeratedWords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBuildingId]);

  const fetchBuildings = async () => {
    try {
      const response = await getBuildingNames();
      const buildings = response?.data?.buildings || [];
      const mappedBuildings: BuildingOption[] = buildings.map(
        ({ uuid, name }: { uuid: string; name: string }) => ({
          id: uuid,
          name
        })
      );
      setBuildingOptions(mappedBuildings);
    } catch (error) {
      toast(error?.message || 'Unable to load buildings');
    }
  };

  const fetchForbiddenAndModeratedWords = async (refresh: boolean = false) => {
    const buildingPayload =
      selectedBuildingId === GLOBAL_SCOPE ? null : selectedBuildingId;

    setLoadingConfig({
      flag: true,
      comment: refresh
        ? CobuConstants.manageWords.refreshing
        : CobuConstants.manageWords.fetching
    });

    try {
      const { data } = await getForbiddenAndModeratedWords(buildingPayload);
      setForbiddenWords(data.forbiddenWords || []);
      setModeratedWords(data.moderatedWords || []);
      setBlockedWords(data.blockedWords || []);
      setAlwaysHideWords(data.alwaysHideWords || []);
      setLoadingConfig({ flag: false });
    } catch (error) {
      setLoadingConfig({ flag: false });
      if (error) {
        toast(error?.message);
      }
    }
  };

  const createWord = async (
    term: string,
    type: WordCategory,
    buildingId?: string | null,
    language: WordLanguage = 'en'
  ) => {
    const scopedBuildingId =
      buildingId === undefined ? selectedBuildingId : buildingId;
    const buildingPayload =
      scopedBuildingId === GLOBAL_SCOPE ? null : scopedBuildingId;

    setLoadingConfig({
      flag: true,
      comment: WORD_CATEGORY_LABELS[type].creating
    });

    try {
      if (type === 'blocked') {
        await createBlockedWord(term, buildingPayload, language);
      } else {
        await createModeratedWord(term, type, buildingPayload, language);
      }
      await fetchForbiddenAndModeratedWords(true);
      toast(WORD_CATEGORY_LABELS[type].createSuccess);
    } catch (error) {
      setLoadingConfig({ flag: false });
      if (error) {
        toast(error?.message);
      }
    }
  };

  const updateWord = async (
    term: string,
    type: WordCategory,
    uuid: string,
    buildingId?: string | null,
    language?: WordLanguage
  ) => {
    const scopedBuildingId =
      buildingId === undefined ? selectedBuildingId : buildingId;
    const buildingPayload =
      scopedBuildingId === GLOBAL_SCOPE ? null : scopedBuildingId;

    setLoadingConfig({
      flag: true,
      comment: WORD_CATEGORY_LABELS[type].editing
    });
    try {
      if (type === 'blocked') {
        await editBlockedWord(term, uuid, buildingPayload, language);
      } else {
        await editModeratedWord(term, type, uuid, buildingPayload, language);
      }

      await fetchForbiddenAndModeratedWords(true);
      toast(WORD_CATEGORY_LABELS[type].editSuccess);
    } catch (error) {
      setLoadingConfig({ flag: false });
      if (error) {
        toast(error?.message);
      }
    }
  };

  const deleteWord = async (
    type: WordCategory,
    uuid: string,
    buildingId?: string | null
  ) => {
    const scopedBuildingId =
      buildingId === undefined ? selectedBuildingId : buildingId;
    const buildingPayload =
      scopedBuildingId === GLOBAL_SCOPE ? null : scopedBuildingId;

    setLoadingConfig({
      flag: true,
      comment: WORD_CATEGORY_LABELS[type].deleting
    });
    try {
      if (type === 'blocked') {
        await deleteBlockedWord(uuid);
      } else {
        await deleteModeratedWord(uuid, type, buildingPayload);
      }

      await fetchForbiddenAndModeratedWords(true);
      toast(WORD_CATEGORY_LABELS[type].deleteSuccess);
    } catch (error) {
      setLoadingConfig({ flag: false });
      if (error) {
        toast(error?.message);
      }
    }
  };

  return {
    buildingOptions,
    selectedBuildingId,
    setSelectedBuildingId,
    forbiddenWords,
    moderatedWords,
    blockedWords,
    alwaysHideWords,
    loadingConfig,
    createWord,
    updateWord,
    deleteWord
  };
}
